/**
 * resolveKeviaMood.js
 *
 * Canonical mood resolver for Kevia.
 * Input: entityContext (from composeKeviaContext) + optional runtime state.
 * Output: { mood, confidence, animationProfile }
 *
 * Mood drives KeviaAvatar CSS animations — no visual logic lives here,
 * only the semantic derivation.
 */

/** @typedef {"neutral"|"reflective"|"urgent"|"celebration"|"grief"|"pastoral-care"|"deep-focus"} KeviaMood */

/**
 * Animation timing profiles per mood.
 * Consumed by KeviaAvatar as data — CSS handles the visual.
 */
const ANIMATION_PROFILES = {
  neutral:         { duration: "7s",  intensity: "low"    },
  reflective:      { duration: "9s",  intensity: "low"    },
  urgent:          { duration: "4.5s",intensity: "medium" },
  celebration:     { duration: "5s",  intensity: "medium" },
  grief:           { duration: "12s", intensity: "low"    },
  "pastoral-care": { duration: "8s",  intensity: "low"    },
  "deep-focus":    { duration: "4s",  intensity: "medium" },
};

const DEFAULT_RESULT = {
  mood:             "neutral",
  confidence:       0,
  animationProfile: ANIMATION_PROFILES.neutral,
};

/**
 * Derive mood from entity context and optional streaming state.
 *
 * @param {object|null} entityContext — output from composeKeviaContext()
 * @param {{ isStreaming?: boolean, hasError?: boolean }} [runtimeState]
 * @returns {{ mood: KeviaMood, confidence: number, animationProfile: object }}
 */
export function resolveKeviaMood(entityContext, runtimeState = {}) {
  const { isStreaming = false, hasError = false } = runtimeState;

  // Streaming → deep-focus, regardless of entity context
  if (isStreaming) {
    return {
      mood:             "deep-focus",
      confidence:       1,
      animationProfile: ANIMATION_PROFILES["deep-focus"],
    };
  }

  if (hasError) {
    return {
      mood:             "reflective",
      confidence:       0.5,
      animationProfile: ANIMATION_PROFILES.reflective,
    };
  }

  if (!entityContext) return DEFAULT_RESULT;

  // Entity context carries a pre-derived mood from composeKeviaContext
  const ctxMood = entityContext.mood;
  if (!ctxMood || ctxMood === "neutral") return DEFAULT_RESULT;

  // Confidence = function of insight quality
  const insights    = entityContext.insights ?? [];
  const highCount   = insights.filter((i) => i.severity === "high"   || i.severity === "critical").length;
  const mediumCount = insights.filter((i) => i.severity === "medium").length;
  const rawConf     = Math.min(1, (highCount * 0.4 + mediumCount * 0.2 + 0.2));
  const confidence  = +rawConf.toFixed(2);

  return {
    mood:             ctxMood,
    confidence,
    animationProfile: ANIMATION_PROFILES[ctxMood] ?? ANIMATION_PROFILES.neutral,
  };
}
